import { useFetcher } from '@remix-run/react'
import { type Editor, type NodeViewProps, useEditor } from '@tiptap/react'
import { type Dispatch, useEffect, useState } from 'react'
import { type tabs } from '#app/components/ui/customizer-bar/editor-bar-email.js'
import { type CustomizerData } from '#app/utils/customizer.client.js'
import { ExtensionKit } from '../extensions/extension-kit'

export type EditorAttributes = {
	mode: 'email' | 'page'
}

export const getCustomizerWidth = (props: NodeViewProps) => {
	const { mode } = props.editor.options.editorProps
		.attributes as EditorAttributes
	return mode === 'page' ? 420 : 600
}

export interface CustomizerProps {
	content?: string
	editable: boolean
	data?: CustomizerData
	vars?: {
		sub?: boolean
		list?: boolean
		user?: boolean
	}
	targetId: string
	setTab?: Dispatch<React.SetStateAction<tabs>>
	mode: 'email' | 'page'
}

declare global {
	interface Window {
		editor: Editor | null
	}
}

export const useCustomizer = ({
	content = '',
	editable = true,
	data,
	vars,
	setTab,
	mode,
	targetId,
}: CustomizerProps) => {
	const [LOADED, setLoaded] = useState(false)
	const [HTML, setHTML] = useState(content)
	const saveContentFetcher = useFetcher()
	const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null)
	const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(
		null,
	)
	const timeout = 1000

	const [isSaved, setIsSaved] = useState(false)

	useEffect(() => {
		if (editable) {
			if (saveContentFetcher.data) {
				if (saveTimeout) {
					clearTimeout(saveTimeout)
				}

				setIsSaved(true)

				setSaveTimeout(
					setTimeout(() => {
						setIsSaved(false)
					}, timeout),
				)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveContentFetcher.data])

	useEffect(() => {
		if (editable) {
			if (typingTimeout) {
				clearTimeout(typingTimeout)
			}
			setTypingTimeout(
				setTimeout(() => {
					saveContentFetcher.submit(
						{ content_st: HTML, _action: 'publish_staging', targetId, mode },
						{ method: 'POST' },
					)
				}, timeout),
			)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [HTML])

	const editor = useEditor(
		{
			content,
			onUpdate: ({ editor }) => {
				setHTML(editor.getHTML())
			},
			autofocus: false,
			immediatelyRender: false,
			editable,
			extensions: [...ExtensionKit({ editable, vars, data, setTab })],
			editorProps: {
				attributes: {
					autocomplete: 'off',
					autocorrect: 'off',
					autocapitalize: 'off',
					class: 'min-h-full',
					mode,
				},
			},
		},
		[targetId],
	)

	LOADED && (window.editor = editor)

	useEffect(() => {
		setLoaded(true)
	}, [])

	return {
		editor,
		isSaved,
		HTML,
	}
}
